import type { RenderContext, VideoFrame } from '../../types';
import { BaseEffect } from '../base';
import { effectSeed, withOverlay } from './composite';
import { hash01 } from '../../utils/deterministic';

const DEFAULTS = { frequency: 0.35, brightness: 1.1 };

function drawBolt(
  ctx: CanvasRenderingContext2D,
  seed: number,
  startX: number,
  startY: number,
  endY: number,
  wobble: number,
  strength: number,
  branchDepth: number,
): void {
  ctx.beginPath();
  let x = startX;
  let y = startY;
  ctx.moveTo(x, y);
  const segments: Array<{ x: number; y: number }> = [{ x, y }];
  while (y < endY) {
    x += (hash01(Math.floor(y), Math.floor(x), seed) - 0.5) * wobble;
    y += 14 + hash01(Math.floor(y), 3, seed) * 28;
    segments.push({ x, y });
    ctx.lineTo(x, y);
  }

  // Outer glow
  ctx.strokeStyle = `rgba(160,190,255,${strength * 0.35})`;
  ctx.lineWidth = 8 + strength * 6;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();

  // Core
  ctx.strokeStyle = `rgba(245,250,255,${Math.min(1, strength)})`;
  ctx.lineWidth = 1.5 + strength * 1.8;
  ctx.stroke();

  if (branchDepth <= 0) return;
  for (let i = 2; i < segments.length - 2; i += 3) {
    if (hash01(i, branchDepth, seed) < 0.55) continue;
    const s = segments[i];
    drawBolt(
      ctx,
      seed + i * 13,
      s.x,
      s.y,
      s.y + (endY - s.y) * (0.35 + hash01(i, 9, seed) * 0.4),
      wobble * 0.7,
      strength * 0.55,
      branchDepth - 1,
    );
  }
}

export class LightningEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'lightning', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    return this.renderCpu(frame, params, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const seed = effectSeed(context, this.id);
    const flash = hash01(context.frameIndex, 0, seed);
    const chance = params.frequency * 0.18;
    if (flash > chance) return frame;

    const strength = params.brightness * (1 - flash / (chance + 0.001));
    return withOverlay(frame, (ctx, w, h) => {
      // Atmospheric flash
      const sky = ctx.createRadialGradient(w * 0.45, 0, 0, w * 0.45, 0, Math.max(w, h) * 0.9);
      sky.addColorStop(0, `rgba(210,225,255,${strength * 0.55})`);
      sky.addColorStop(0.5, `rgba(160,185,240,${strength * 0.22})`);
      sky.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, w, h);

      const boltCount = 1 + (hash01(context.frameIndex, 2, seed) > 0.7 ? 1 : 0);
      for (let b = 0; b < boltCount; b += 1) {
        const startX = w * (0.2 + hash01(b, context.frameIndex, seed) * 0.6);
        drawBolt(ctx, seed + b * 97, startX, 0, h * (0.55 + hash01(b, 4, seed) * 0.4), 55, strength, 2);
      }
    }, 'lighter');
  }
}
